import cx from 'classnames';
import { useTranslation } from 'react-i18next';
import styles from '../sass/components/PaymentPreferenceSelector.module.scss';
import {
  formatCents,
  PaymentPreference,
  PaymentPreferenceValue,
} from '../util/bookingPricing';

/**
 * "Send the bill to your partner": a choice on this step only. The booking
 * it makes is a prepay one (PaymentPreference.prepay) whose pay link is
 * emailed to the payer.
 */
export const PAYER_CHOICE = 'PAYER' as const;
export type PaymentChoice = PaymentPreferenceValue | typeof PAYER_CHOICE;

interface PaymentPreferenceSelectorProps {
  value: PaymentChoice;
  onChange: (value: PaymentChoice) => void;
  /** Prepay discount percentage, e.g. 3. */
  prepayDiscountPercent: number;
  /** Amount (cents, positive) the customer saves by prepaying. */
  prepaySavingsCents: number;
  disabled?: boolean;
  /**
   * Render "Prepay & save" as the first (left) option. Off by default:
   * pay-later first is what every tenant gets, the Aperio preset included
   * since it defaults to pay-later (owner, 2026-10).
   */
  prepayFirst?: boolean;
  /**
   * False when prepay cannot be offered for this booking (nobody serves the
   * address): the prepay option, and its discount, are not shown at all.
   */
  prepayAvailable?: boolean;
  /**
   * Offer "Send the bill to your partner" as a third, full-width option
   * under the other two. Off by default: only the Aperio preset turns it
   * on, and never where prepay is not available.
   */
  payerAvailable?: boolean;
}

/**
 * "Pay after service" vs "Prepay and save" choice shown on the booking step,
 * plus, where the step asks for it (Aperio), "Send the bill to your partner"
 * as a third option in the same radio group. None of them collects payment
 * during booking.
 */
export default function PaymentPreferenceSelector({
  value,
  onChange,
  prepayDiscountPercent,
  prepaySavingsCents,
  disabled = false,
  prepayFirst = false,
  prepayAvailable = true,
  payerAvailable = false,
}: PaymentPreferenceSelectorProps) {
  const { t } = useTranslation();
  const percent = prepayDiscountPercent;
  const payLaterOption = {
    value: PaymentPreference.payLater,
    title: t('payment.later.title'),
    badge: t('summary.noPaymentNow'),
    description: t('payment.later.description'),
  };
  const prepayOption = {
    value: PaymentPreference.prepay,
    title: t('payment.prepay.title', { percent }),
    badge:
      prepaySavingsCents > 0
        ? t('payment.prepay.save', { amount: formatCents(prepaySavingsCents) })
        : t('payment.prepay.savePercent', { percent }),
    description: t('payment.prepay.description', { percent }),
  };
  const options: Array<{
    value: PaymentChoice;
    title: string;
    badge: string;
    description: string;
  }> = [payLaterOption, prepayOption].filter(
    (option) => prepayAvailable || option !== prepayOption,
  );
  if (prepayFirst && options.length > 1) options.reverse();
  // Prepay pricing, sent to someone else; only where prepay itself is.
  const withPayer = payerAvailable && prepayAvailable;
  if (withPayer) {
    options.push({
      value: PAYER_CHOICE,
      title: t('payment.payer.title'),
      // The saving holds only if they pay ahead; unpaid, it is the
      // regular price at the visit.
      badge:
        prepaySavingsCents > 0
          ? t('payment.payer.save', {
            amount: formatCents(prepaySavingsCents),
          })
          : t('payment.payer.savePercent', { percent }),
      description: t('payment.payer.description', { percent }),
    });
  }

  return (
    <div
      // `apr-pay-choice*` are hooks for the Aperio theme (the third option
      // spans under the other two); absent unless the payer option is on.
      className={cx(styles.selector, { 'apr-pay-choice': withPayer })}
      role="radiogroup"
      aria-label={t('schedule.pay.title')}
    >
      {options.map((option) => {
        const selected = value === option.value;
        const id = `payment-preference-${option.value.toLowerCase()}`;
        const payer = option.value === PAYER_CHOICE;
        return (
          <label
            key={option.value}
            htmlFor={id}
            className={cx(styles.option, {
              [styles.selected]: selected,
              [styles.disabled]: disabled,
              'apr-pay-choice__payer': payer,
              'apr-pay-choice__payer--idle': payer && !selected,
            })}
          >
            <input
              id={id}
              className={styles.nativeInput}
              type="radio"
              name="paymentPreference"
              value={option.value}
              checked={selected}
              disabled={disabled}
              onChange={() => onChange(option.value)}
            />
            {/* Custom indicator: the native radio is restyled globally and
                rendered as a plain grey square, so draw our own check. */}
            <span
              className={cx(styles.indicator, {
                [styles.indicatorSelected]: selected,
              })}
              aria-hidden="true"
            >
              {selected && (
                <svg
                  viewBox="0 0 16 16"
                  width="12"
                  height="12"
                  focusable="false"
                >
                  <path
                    d="M2.5 8.5l3.2 3.2L13.5 4"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2.4"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              )}
            </span>
            <span className={styles.optionBody}>
              <span className={styles.optionHeader}>
                <span className={styles.optionTitle}>{option.title}</span>
                <span
                  className={cx(styles.badge, {
                    [styles.badgeSavings]:
                      option.value === PaymentPreference.prepay || payer,
                  })}
                >
                  {option.badge}
                </span>
              </span>
              <span className={styles.optionDescription}>
                {option.description}
              </span>
            </span>
          </label>
        );
      })}
    </div>
  );
}
